/**
 * Logica pura do modal "Selecionar URLs" (UrlSelectorDialog).
 *
 * Fica fora do componente para ser testavel sem DOM: a regra de ouro e que
 * a mesma URL NUNCA aparece duas vezes na lista - nem como "Manual" e
 * "automatica" ao mesmo tempo, nem como "/foo" e "/foo/".
 */

export interface ApiUrl {
    path: string;
    post_id: string | null;
    pageviews: number;
}

export interface UrlEntry {
    path: string;
    post_id: string | null;
    pageviews: number;
    manual: boolean;
}

/**
 * Normaliza um path digitado ou vindo da API para um formato unico:
 * - aceita URL completa colada (https://dominio.com/path/)
 * - descarta query string e hash
 * - garante barra inicial
 * - garante barra final (mesmo formato da API de pageviews), exceto quando
 *   o ultimo segmento parece arquivo (tem ponto)
 *
 * Devolve '' para entrada vazia.
 */
export function normalizePath(raw: string): string {
    let p = (raw ?? '').trim();
    if (!p) return '';
    p = p.replace(/^https?:\/\/[^/]+/i, '');
    p = p.split('?')[0].split('#')[0];
    if (!p.startsWith('/')) p = '/' + p;
    const last = p.split('/').pop() || '';
    if (!p.endsWith('/') && !last.includes('.')) p = p + '/';
    return p;
}

/** Paths da API ja normalizados - fonte unica pra checar duplicata. */
export function buildApiPathSet(apiUrls: ApiUrl[]): Set<string> {
    return new Set(apiUrls.map((u) => normalizePath(u.path) || u.path));
}

/**
 * Dentre os paths ja salvos no item, quais NAO vieram da API (sao manuais).
 * Compara normalizado, entao um salvo como "/foo" casa com "/foo/" da API.
 */
export function detectManualPaths(
    savedPaths: { path: string }[],
    apiPathSet: Set<string>
): Set<string> {
    const manual = new Set<string>();
    savedPaths.forEach((p) => {
        const path = normalizePath(p.path) || p.path;
        if (!apiPathSet.has(path)) manual.add(path);
    });
    return manual;
}

/**
 * Lista combinada com UMA entrada por path.
 *
 * Se um path manual tambem veio da API, a entrada da API (post_id +
 * pageviews) prevalece - mas mantem a posicao do manual (topo da lista),
 * pra quem acabou de adicionar continuar vendo o item.
 */
export function mergeUrlEntries(
    apiUrls: ApiUrl[],
    manualPaths: Iterable<string>
): UrlEntry[] {
    const byPath = new Map<string, UrlEntry>();
    for (const path of manualPaths) {
        byPath.set(path, { path, post_id: null, pageviews: 0, manual: true });
    }
    for (const u of apiUrls) {
        const path = normalizePath(u.path) || u.path;
        byPath.set(path, {
            path,
            post_id: u.post_id,
            pageviews: u.pageviews,
            manual: false,
        });
    }
    return Array.from(byPath.values());
}

export type ManualAddResult =
    | { kind: 'invalid_path' }
    | { kind: 'invalid_weight' }
    /** Ja existia como automatica: nao cria entrada manual, so aplica o peso. */
    | { kind: 'exists_auto'; path: string; weight: number }
    /** Ja existia como manual: so atualiza o peso. */
    | { kind: 'exists_manual'; path: string; weight: number }
    | { kind: 'added'; path: string; weight: number };

/**
 * Decide o que fazer quando o usuario adiciona um path manual.
 * Em qualquer caso valido o peso manual explicito prevalece sobre o auto
 * (RPS) - e a unica prioridade que faz sentido: quem digita um peso quer
 * aquele peso.
 */
export function resolveManualAdd(
    rawPath: string,
    rawWeight: string,
    apiPathSet: Set<string>,
    manualPaths: Set<string>
): ManualAddResult {
    const path = normalizePath(rawPath);
    if (!path || path === '/') return { kind: 'invalid_path' };

    const weight = parseFloat(rawWeight);
    if (isNaN(weight) || weight <= 0) return { kind: 'invalid_weight' };

    if (apiPathSet.has(path)) return { kind: 'exists_auto', path, weight };
    if (manualPaths.has(path)) return { kind: 'exists_manual', path, weight };
    return { kind: 'added', path, weight };
}
